/**
 * useThreadList — TanStack Query data layer for the chat history sidebar
 * (STORY-002-010 step 2/3).
 *
 * Server state (the thread list) lives in TanStack Query; client state (active
 * thread, scroll offsets) lives in the Zustand `chat-store`. This module owns:
 *
 *   - `useThreadList(group)` — cursor-paginated infinite query over
 *     `GET /chat/threads?group=&cursor=` (page size 20, AC#7). Cursor pagination
 *     only — no offset (Addendum §6.4 / story guardrail). RU/KY/EN agnostic
 *     (server returns the thread's own language).
 *   - `useRenameThread()` / `usePinThread()` / `useDeleteThread()` — optimistic
 *     mutations that update the cached pages immediately and roll back on error
 *     (AC#2/#3/#4/#6 / story guardrail "optimistic updates with rollback").
 *
 * No PII reaches a client log or a Sentry breadcrumb: the only message text
 * carried here is `last_message_snippet`, which the server already redacts
 * (`content_redacted`) — the raw content never travels in this list (AC#13).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
} from "@tanstack/react-query";

import { apiClient, apiFetch } from "@/lib/api-client";

/** One thread row (mirrors backend `ThreadListItem`, STORY-002-010). */
export interface ThreadListItem {
  id: string;
  title: string | null;
  language: string;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  is_pinned: boolean;
  last_message_snippet: string | null;
}

/** One page of `GET /chat/threads` (mirrors backend `ThreadListResponse`). */
export interface ThreadListPage {
  threads: ThreadListItem[];
  next_cursor: string | null;
}

/** Sidebar time bucket (mirrors backend `ThreadGroup`). */
export type ThreadGroup = "last_7_days" | "older";

export const THREADS_PAGE_SIZE = 20;

/**
 * Sidebar title-poll cadence. The first AI title is generated asynchronously by
 * the worker a few seconds after a chat starts (outbox drain + gpt-4o-mini
 * call), so a brand-new thread first appears with `title: null`. While any
 * recently-created thread is still untitled we refetch every
 * {@link TITLE_POLL_INTERVAL_MS} so the generated title shows up without a
 * manual reload; once all are titled we stop polling.
 */
export const TITLE_POLL_INTERVAL_MS = 3_000;

/**
 * Only poll for titles on threads created within this window. Pre-feature
 * threads (and any thread whose title generation legitimately produced nothing)
 * keep `title: null` forever — without this bound the sidebar would poll every
 * few seconds indefinitely for those users.
 */
export const TITLE_PENDING_WINDOW_MS = 120_000;

/**
 * `refetchInterval` value (ms) for the thread list, or `false` to stop. Returns
 * the poll interval while at least one recently-created thread is still awaiting
 * its async AI title; `false` otherwise. Pure + time-injected so it is trivially
 * testable.
 */
export function titlePollInterval(
  pages: readonly ThreadListPage[],
  nowMs: number,
): number | false {
  const awaitingTitle = pages.some((page) =>
    page.threads.some(
      (t) =>
        (t.title === null || t.title.length === 0) &&
        nowMs - Date.parse(t.created_at) < TITLE_PENDING_WINDOW_MS,
    ),
  );
  return awaitingTitle ? TITLE_POLL_INTERVAL_MS : false;
}

/** Stable query key so mutations can target the right cache entry. */
export function threadListQueryKey(group?: ThreadGroup): readonly unknown[] {
  return group === undefined ? ["chat", "threads"] : ["chat", "threads", group];
}

function buildPath(group: ThreadGroup | undefined, cursor: string | null): string {
  const params = new URLSearchParams();
  params.set("limit", String(THREADS_PAGE_SIZE));
  if (group !== undefined) params.set("group", group);
  if (cursor !== null) params.set("cursor", cursor);
  return `/chat/threads?${params.toString()}`;
}

/**
 * Infinite query over the thread list. `pageParam` is the opaque cursor
 * (`null` for the first page). `getNextPageParam` reads `next_cursor` — `null`
 * on the last page stops pagination (`hasNextPage` becomes false).
 */
export function useThreadList(
  group?: ThreadGroup,
): UseInfiniteQueryResult<InfiniteData<ThreadListPage, string | null>, Error> {
  return useInfiniteQuery({
    queryKey: threadListQueryKey(group),
    queryFn: ({ pageParam }): Promise<ThreadListPage> =>
      apiClient.get<ThreadListPage>(buildPath(group, pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage): string | null => lastPage.next_cursor,
    // Poll while a freshly-created thread is still awaiting its async AI title
    // (see titlePollInterval); stops automatically once all are titled.
    refetchInterval: (query): number | false =>
      titlePollInterval(query.state.data?.pages ?? [], Date.now()),
  });
}

/** Run `fn` over every cached page of the thread list (any group). */
function mapAllThreadPages(
  queryClient: ReturnType<typeof useQueryClient>,
  fn: (threads: ThreadListItem[]) => ThreadListItem[],
): { key: readonly unknown[]; data: InfiniteData<ThreadListPage, string | null> }[] {
  const snapshots = queryClient.getQueriesData<
    InfiniteData<ThreadListPage, string | null>
  >({ queryKey: ["chat", "threads"] });
  const previous: {
    key: readonly unknown[];
    data: InfiniteData<ThreadListPage, string | null>;
  }[] = [];
  for (const [key, data] of snapshots) {
    if (data === undefined) continue;
    previous.push({ key, data });
    queryClient.setQueryData<InfiniteData<ThreadListPage, string | null>>(key, {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        threads: fn(page.threads),
      })),
    });
  }
  return previous;
}

/** Restore the pre-mutation snapshots captured by {@link mapAllThreadPages}. */
function restoreThreadPages(
  queryClient: ReturnType<typeof useQueryClient>,
  previous: {
    key: readonly unknown[];
    data: InfiniteData<ThreadListPage, string | null>;
  }[],
): void {
  for (const { key, data } of previous) {
    queryClient.setQueryData(key, data);
  }
}

type ThreadPagesSnapshot = {
  key: readonly unknown[];
  data: InfiniteData<ThreadListPage, string | null>;
}[];

export interface RenameThreadVars {
  threadId: string;
  title: string;
}

/**
 * Optimistic rename (AC#6). Updates the title in every cached page before the
 * request resolves; rolls back to the snapshot on error so the user never sees
 * a stale-but-wrong title.
 */
export function useRenameThread(): UseMutationResult<
  ThreadListItem,
  Error,
  RenameThreadVars,
  ThreadPagesSnapshot
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, title }): Promise<ThreadListItem> =>
      apiFetch<ThreadListItem, { title: string }>({
        method: "PATCH",
        path: `/chat/threads/${threadId}`,
        body: { title },
      }),
    onMutate: async ({ threadId, title }) => {
      await queryClient.cancelQueries({ queryKey: ["chat", "threads"] });
      return mapAllThreadPages(queryClient, (threads) =>
        threads.map((t) => (t.id === threadId ? { ...t, title } : t)),
      );
    },
    onError: (_err, _vars, context) => {
      if (context) restoreThreadPages(queryClient, context);
    },
  });
}

export interface PinThreadVars {
  threadId: string;
  isPinned: boolean;
}

/**
 * Optimistic pin/unpin (AC#2/#3). Flips `is_pinned` in every cached page; rolls
 * back on error. Re-sorting into the "Pinned" section is the component's job —
 * it reads `is_pinned` when it buckets the rows, so the flip is enough.
 */
export function usePinThread(): UseMutationResult<
  ThreadListItem,
  Error,
  PinThreadVars,
  ThreadPagesSnapshot
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, isPinned }): Promise<ThreadListItem> =>
      apiFetch<ThreadListItem, { is_pinned: boolean }>({
        method: "PATCH",
        path: `/chat/threads/${threadId}/pin`,
        body: { is_pinned: isPinned },
      }),
    onMutate: async ({ threadId, isPinned }) => {
      await queryClient.cancelQueries({ queryKey: ["chat", "threads"] });
      return mapAllThreadPages(queryClient, (threads) =>
        threads.map((t) =>
          t.id === threadId ? { ...t, is_pinned: isPinned } : t,
        ),
      );
    },
    onError: (_err, _vars, context) => {
      if (context) restoreThreadPages(queryClient, context);
    },
  });
}

/**
 * Optimistic delete (AC#4). Removes the thread from every cached page; restores
 * it on error so the user gets it back if the server rejected the delete.
 */
export function useDeleteThread(): UseMutationResult<
  void,
  Error,
  string,
  ThreadPagesSnapshot
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId): Promise<void> =>
      apiClient.delete<void>(`/chat/threads/${threadId}`),
    onMutate: async (threadId) => {
      await queryClient.cancelQueries({ queryKey: ["chat", "threads"] });
      return mapAllThreadPages(queryClient, (threads) =>
        threads.filter((t) => t.id !== threadId),
      );
    },
    onError: (_err, _threadId, context) => {
      if (context) restoreThreadPages(queryClient, context);
    },
  });
}
